import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Button,
  Input,
  Label,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
//import { getUserModerationList } from "../userModeration/helper/userModerationApi";
import { getUserModerationList, getBadgeList, addBadgeToUser, removeBadgeFromUser } from "./userBadgeApi";

const UserBadgePage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter states
  const [username, setUsername] = useState("");
  const [userId, setUserId] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  // Badge modal states
  const [badgeList, setBadgeList] = useState([]);
  const [addBadgeModal, setAddBadgeModal] = useState(false);
  const [removeBadgeModal, setRemoveBadgeModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBadgeId, setSelectedBadgeId] = useState("");
  const [selectedRemoveBadgeId, setSelectedRemoveBadgeId] = useState("");
  const [badgeLoading, setBadgeLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Refs for optimization
  const usernameTimeoutRef = useRef(null);
  const userIdTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const hasInitialFetchedRef = useRef(false);

  // Debounced values
  const [debouncedUsername, setDebouncedUsername] = useState(null);
  const [debouncedUserId, setDebouncedUserId] = useState(null);

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">Username</span>,
        selector: (row) => row.username,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">User ID</span>,
        selector: (row) => row.userId,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Phone Verified</span>,
        selector: (row) => row.phoneVerified,
        sortable: true,
        cell: (row) => (
          <Badge color={row.phoneVerified ? "success" : "danger"}>
            {row.phoneVerified ? "Yes" : "No"}
          </Badge>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Action</span>,
        width: "250px",
        cell: (row) => (
          <div className="d-flex gap-2">
            <Button
              color="primary"
              size="sm"
              onClick={() => handleAddTag(row)}
              className="px-3"
            >
              Add Tag
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => handleRemoveBadge(row)}
              className="px-3"
            >
              Remove Badge
            </Button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    []
  );

  const handleAddTag = useCallback((user) => {
    setSelectedUser(user);
    setSelectedBadgeId("");
    setAddBadgeModal(true);
  }, []);

  const handleRemoveBadge = useCallback((user) => {
    setSelectedUser(user);
    setSelectedRemoveBadgeId("");
    setRemoveBadgeModal(true);
  }, []);

  const toggleAddBadgeModal = useCallback(() => {
    setAddBadgeModal(!addBadgeModal);
    if (!addBadgeModal) {
      setSelectedUser(null);
      setSelectedBadgeId("");
    }
  }, [addBadgeModal]);

  const toggleRemoveBadgeModal = useCallback(() => {
    setRemoveBadgeModal(!removeBadgeModal);
    if (!removeBadgeModal) {
      setSelectedUser(null);
      setSelectedRemoveBadgeId("");
    }
  }, [removeBadgeModal]);

  const handleBadgeSubmit = useCallback(async () => {
    if (!selectedUser || !selectedBadgeId) {
      alert("Please select a badge");
      return;
    }

    setSubmitting(true);
    try {
      const result = await addBadgeToUser({
        userIds: [selectedUser.userId],
        badge_id: parseInt(selectedBadgeId),
      });

      // Show success/error message based on response
      if (result?.success) {
        const summary = result?.result?.summary || {};
        const results = result?.result?.results || {};
        
        if (summary.successful > 0) {
          alert(`Badge added successfully!`);
        } else if (summary.failed > 0 && results.failed && results.failed.length > 0) {
          const errorMsg = results.failed[0]?.error || "Failed to add badge";
          alert(`Error: ${errorMsg}`);
        } else {
          alert(`Badge assignment processed. Check results for details.`);
        }
        
        toggleAddBadgeModal();
        // Optionally refresh the user list
        // fetchList();
      } else {
        alert("Failed to add badge. Please try again.");
      }
    } catch (error) {
      console.error("Error adding badge to user:", error);
      alert(error?.response?.data?.message || "Failed to add badge. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [selectedUser, selectedBadgeId, toggleAddBadgeModal]);

  const handleRemoveBadgeSubmit = useCallback(async () => {
    if (!selectedUser || !selectedRemoveBadgeId) {
      alert("Please select a badge to remove");
      return;
    }

    setRemoving(true);
    try {
      const result = await removeBadgeFromUser({
        userIds: [selectedUser.userId],
        badge_id: parseInt(selectedRemoveBadgeId),
      });

      // Show success/error message based on response
      if (result?.success) {
        const summary = result?.result?.summary || {};
        const results = result?.result?.results || {};
        
        if (summary.successful > 0) {
          alert(`Badge removed successfully!`);
        } else if (summary.failed > 0 && results.failed && results.failed.length > 0) {
          const errorMsg = results.failed[0]?.error || "Failed to remove badge";
          alert(`Error: ${errorMsg}`);
        } else {
          alert(`Badge removal processed. Check results for details.`);
        }
        
        toggleRemoveBadgeModal();
        // Optionally refresh the user list
        // fetchList();
      } else {
        alert("Failed to remove badge. Please try again.");
      }
    } catch (error) {
      console.error("Error removing badge from user:", error);
      alert(error?.response?.data?.message || "Failed to remove badge. Please try again.");
    } finally {
      setRemoving(false);
    }
  }, [selectedUser, selectedRemoveBadgeId, toggleRemoveBadgeModal]);

  const fetchList = useCallback(async (debouncedUsername = null, debouncedUserId = null) => {
    if (isLoadingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const filters = {
        limit,
        page: currentPage,
        keyword: debouncedUsername !== null ? debouncedUsername : (username || undefined),
        userId: debouncedUserId !== null ? debouncedUserId : (userId || undefined),
        dateRange: dateRange || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };

      const result = await getUserModerationList(filters);

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      setData(result.data);
      setTotalRows(result.pagination.totalRecords);
    } catch (error) {
      if (error.name !== "AbortError" && isMountedRef.current) {
        console.error("Error fetching user list:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
        isLoadingRef.current = false;
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [username, userId, dateRange, startDate, endDate, currentPage, limit]);

  // Fetch badge list on page load
  const fetchBadgeList = useCallback(async () => {
    setBadgeLoading(true);
    try {
      const result = await getBadgeList();
      setBadgeList(result.data || []);
    } catch (error) {
      console.error("Error fetching badge list:", error);
    } finally {
      setBadgeLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    if (!hasInitialFetchedRef.current) {
      fetchList();
      fetchBadgeList();
      hasInitialFetchedRef.current = true;
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (usernameTimeoutRef.current) {
        clearTimeout(usernameTimeoutRef.current);
      }
      if (userIdTimeoutRef.current) {
        clearTimeout(userIdTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce username search
  useEffect(() => {
    if (usernameTimeoutRef.current) {
      clearTimeout(usernameTimeoutRef.current);
    }

    usernameTimeoutRef.current = setTimeout(() => {
      setDebouncedUsername(username);
    }, 1000);

    return () => {
      if (usernameTimeoutRef.current) {
        clearTimeout(usernameTimeoutRef.current);
      }
    };
  }, [username]);

  // Debounce userId search
  useEffect(() => {
    if (userIdTimeoutRef.current) {
      clearTimeout(userIdTimeoutRef.current);
    }

    userIdTimeoutRef.current = setTimeout(() => {
      setDebouncedUserId(userId);
    }, 1000);

    return () => {
      if (userIdTimeoutRef.current) {
        clearTimeout(userIdTimeoutRef.current);
      }
    };
  }, [userId]);

  // Fetch when debounced username changes
  useEffect(() => {
    if (hasInitialFetchedRef.current && debouncedUsername !== null) {
      setCurrentPage(1);
      fetchList(debouncedUsername, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUsername]);

  // Fetch when debounced userId changes
  useEffect(() => {
    if (hasInitialFetchedRef.current && debouncedUserId !== null) {
      setCurrentPage(1);
      fetchList(null, debouncedUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUserId]);

  // Fetch when page changes
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Fetch when other filters change
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      setCurrentPage(1);
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, startDate, endDate]);

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1);
    switch (filterType) {
      case "username":
        setUsername(value);
        break;
      case "userId":
        setUserId(value);
        break;
      case "dateRange":
        setDateRange(value);
        if (value !== "custom") {
          setStartDate("");
          setEndDate("");
        }
        break;
      case "startDate":
        setStartDate(value);
        if (value) setDateRange("custom");
        break;
      case "endDate":
        setEndDate(value);
        if (value) setDateRange("custom");
        break;
      default:
        break;
    }
  };

  const clearFilters = useCallback(() => {
    setUsername("");
    setUserId("");
    setDateRange("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="User Badge" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <i className="mdi mdi-badge-account me-2"></i>User Badge
                  </h4>
                </CardHeader>
                <CardBody>
                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <FormGroup>
                        <Label>Username</Label>
                        <Input
                          type="text"
                          placeholder="Search by username..."
                          value={username}
                          onChange={(e) => handleFilterChange("username", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>User ID</Label>
                        <Input
                          type="text"
                          placeholder="Search by user ID..."
                          value={userId}
                          onChange={(e) => handleFilterChange("userId", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Date Range</Label>
                        <Input
                          type="select"
                          value={dateRange}
                          onChange={(e) => handleFilterChange("dateRange", e.target.value)}
                        >
                          <option value="">None</option>
                          <option value="last_24_hours">Last 24 Hours</option>
                          <option value="last_7_days">Last 7 Days</option>
                          <option value="last_30_days">Last 30 Days</option>
                          <option value="custom">Custom Range</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    {(dateRange === "custom" || (startDate || endDate)) && (
                      <>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Start Date</Label>
                            <Input
                              type="date"
                              value={startDate}
                              onChange={(e) => handleFilterChange("startDate", e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>End Date</Label>
                            <Input
                              type="date"
                              value={endDate}
                              onChange={(e) => handleFilterChange("endDate", e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                      </>
                    )}
                    <Col md={12}>
                      <Button color="secondary" onClick={clearFilters} className="me-2">
                        <i className="mdi mdi-filter-remove me-1"></i>Clear Filters
                      </Button>
                    </Col>
                  </Row>

                  {/* Data Table */}
                  <DataTable
                    columns={columns}
                    data={data}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    onChangePage={(page) => setCurrentPage(page)}
                    paginationPerPage={limit}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i className="mdi mdi-account-off" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
                        <p className="mt-3 text-muted">No users found</p>
                      </div>
                    }
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add Badge Modal */}
      <Modal isOpen={addBadgeModal} toggle={toggleAddBadgeModal} size="md">
        <ModalHeader toggle={toggleAddBadgeModal}>
          Add Badge to User
        </ModalHeader>
        <ModalBody>
          {selectedUser && (
            <div className="mb-3">
              <Label className="fw-bold">User:</Label>
              <p className="mb-0">
                <strong>Username:</strong> {selectedUser.username || "N/A"}<br />
                <strong>User ID:</strong> {selectedUser.userId || "N/A"}
              </p>
            </div>
          )}
          <FormGroup>
            <Label for="badgeSelect">Select Badge <span className="text-danger">*</span></Label>
            <Input
              type="select"
              id="badgeSelect"
              value={selectedBadgeId}
              onChange={(e) => setSelectedBadgeId(e.target.value)}
              disabled={badgeLoading || submitting}
            >
              <option value="">-- Select a badge --</option>
              {badgeList.map((badge) => (
                <option key={badge.badge_id} value={badge.badge_id}>
                  {badge.badge_name || badge.badge_key || `Badge ${badge.badge_id}`}
                </option>
              ))}
            </Input>
            {badgeLoading && (
              <small className="text-muted">Loading badges...</small>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={toggleAddBadgeModal}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleBadgeSubmit}
            disabled={!selectedBadgeId || submitting}
          >
            {submitting ? "Adding..." : "Add Badge"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Remove Badge Modal */}
      <Modal isOpen={removeBadgeModal} toggle={toggleRemoveBadgeModal} size="md">
        <ModalHeader toggle={toggleRemoveBadgeModal}>
          Remove Badge from User
        </ModalHeader>
        <ModalBody>
          {selectedUser && (
            <div className="mb-3">
              <Label className="fw-bold">User:</Label>
              <p className="mb-0">
                <strong>Username:</strong> {selectedUser.username || "N/A"}<br />
                <strong>User ID:</strong> {selectedUser.userId || "N/A"}
              </p>
            </div>
          )}
          <FormGroup>
            <Label for="removeBadgeSelect">Select Badge to Remove <span className="text-danger">*</span></Label>
            <Input
              type="select"
              id="removeBadgeSelect"
              value={selectedRemoveBadgeId}
              onChange={(e) => setSelectedRemoveBadgeId(e.target.value)}
              disabled={badgeLoading || removing}
            >
              <option value="">-- Select a badge --</option>
              {badgeList.map((badge) => (
                <option key={badge.badge_id} value={badge.badge_id}>
                  {badge.badge_name || badge.badge_key || `Badge ${badge.badge_id}`}
                </option>
              ))}
            </Input>
            {badgeLoading && (
              <small className="text-muted">Loading badges...</small>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={toggleRemoveBadgeModal}
            disabled={removing}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleRemoveBadgeSubmit}
            disabled={!selectedRemoveBadgeId || removing}
          >
            {removing ? "Removing..." : "Remove Badge"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default UserBadgePage;
